import Prismic from 'prismic-javascript';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Cookies from 'js-cookie';
import queryString from 'query-string';
import startCase from 'lodash/startCase';

import { history } from 'Redux/store';
import { get } from 'Api';
import Format from 'Helpers/Blog/Format';

const { formatPinnedPost } = Format;
const apiEndpoint = 'https://blog-api.fanatical.com/api/v2';

const FIVE_MINUTES = 5 * 60 * 1000;

const PRISMIC_CACHE = {
  api: null,
  timestamp: 0,
};

const linkResolver = (doc) => {
  if (doc.type === 'blog_template_groups') return `/blog/${doc.uid}`;
  return '/';
};

export const clearPreviewCookie = () => {
  const previewCookie = Cookies.get('io.prismic.preview');

  if (previewCookie) {
    Cookies.remove('io.prismic.preview');
    window.location.reload(true);
  }
};

const errorHandler = (ex) => {
  if (ex.status === 404) {
    clearPreviewCookie();
  } else {
    throw ex;
  }
};

/**
 * Check cache and return API instance or fetch fresh
 * @return {Promise} API object
 */
const getApi = async () => {
  if (!PRISMIC_CACHE.api || (PRISMIC_CACHE.timestamp + FIVE_MINUTES) < Date.now()) {
    const data = await Prismic.getApi(apiEndpoint);
    PRISMIC_CACHE.api = data;
    PRISMIC_CACHE.timestamp = Date.now();
  }

  return PRISMIC_CACHE.api;
};

export async function getPosts({
  pageSize = 14,
  page = 0,
  tag,
  searchTerm,
  category,
  authorName,
  guestAuthorName,
}) {
  let posts = [];

  const fetch = [
    'blog_template_groups.title',
    'blog_template_groups.publication_datetime',
    'blog_template_groups.cover',
    'blog_template_groups.intro_text',
    'blog_template_groups.category',
    'blog_template_groups.author',
    'blog_template_groups.guest_author',
  ];
  try {
    const api = await getApi();
    let Predicates = [Prismic.Predicates.not('my.blog_template_groups.pinned', 'true'),
      Prismic.Predicates.at('document.type', 'blog_template_groups')];
    if (tag) {
      Predicates = [Prismic.Predicates.any('document.tags', [tag])];
    }

    if (searchTerm) {
      Predicates.push(Prismic.Predicates.fulltext('my.blog_template_groups.title', searchTerm));
    }

    if (category && category !== 'games') {
      Predicates.push(Prismic.Predicates.at('my.blog_template_groups.category', startCase(category)));
    }

    // games category is the default. Posts without a category are game posts
    if (category && category === 'games') {
      Predicates.push(
        Prismic.Predicates.not('my.blog_template_groups.category', 'News'),
        Prismic.Predicates.not('my.blog_template_groups.category', 'Develop'),
        Prismic.Predicates.not('my.blog_template_groups.category', 'Community'),
      );
    }

    if (authorName) {
      Predicates = [
        Prismic.Predicates.at('my.blog_template_groups.author', authorName),
        Prismic.Predicates.missing('my.blog_template_groups.guest_author'),
      ];
    }

    if (guestAuthorName) {
      Predicates = [Prismic.Predicates.fulltext('my.blog_template_groups.guest_author', guestAuthorName)];
    }

    posts = await api.query(Predicates,
      { fetch, pageSize, page, orderings: '[my.blog_template_groups.publication_datetime desc]' },
    );
  } catch (ex) {
    errorHandler(ex);
  }
  return posts;
}

export async function getPost(slug) {
  let post = {};
  try {
    const api = await getApi();
    const response = await api.getByUID('blog_template_groups', slug);

    if (_get(response, 'data', false)) {
      post = response;
    }
  } catch (ex) { /* */ }

  return post;
}

export async function getRelatedPosts(id, pageSize) {
  let posts = [];

  const fetch = [
    'blog_template_groups.title',
    'blog_template_groups.publication_datetime',
    'blog_template_groups.cover',
  ];
  try {
    const api = await getApi();
    const data = await api.query(
      Prismic.Predicates.similar(id, 20),
      { fetch, pageSize, orderings: '[my.blog_template_groups.publication_datetime desc]' },
    );
    posts = _get(data, 'results', []);
  } catch (ex) {
    errorHandler(ex);
  }
  return posts;
}

export async function fetchBlogPreview(token) {
  try {
    const api = await Prismic.getApi(apiEndpoint);
    const previewUrl = await api.previewSession(token, linkResolver, '/');
    if (!isEmpty(previewUrl)) {
      Cookies.set('io.prismic.preview', token, { expires: FIVE_MINUTES });
      history.push(previewUrl);
    }
  } catch (ex) {
    errorHandler(ex);
  }
}

export async function getBlogSummary() {
  let posts = [];

  try {
    const { response, data } = await get([], '/blog/homecontent');
    if (response.ok) {
      posts = data;
    }
  } catch (ex) {
    errorHandler(ex);
  }

  return posts;
}

export async function getGiftGuidePosts() {
  let posts = [];

  try {
    const { response, data } = await get([], '/blog/gift-guide');
    if (response.ok) {
      posts = data;
    }
  } catch (ex) {
    errorHandler(ex);
  }

  return posts;
}

export async function getPinnedPost() {
  const fetch = [
    'blog_template_groups.title',
    'blog_template_groups.cover',
    'blog_template_groups.intro_text',
  ];
  let post = {};
  try {
    const api = await getApi();
    const posts = await api.query(
      Prismic.Predicates.at('my.blog_template_groups.pinned', 'true'),
      {
        fetch,
        pageSize: 1,
        page: 1,
        orderings: '[my.blog_template_groups.publication_datetime desc]',
      },
    );

    post = formatPinnedPost(_get(posts, 'results[0]', {}));
  } catch (ex) {
    errorHandler(ex);
  }
  return post;
}

export async function getSecondaryPinnedPost() {
  let post = {};
  const fetch = [
    'blog_template_groups.title',
    'blog_template_groups.cover',
    'blog_template_groups.intro_text',
  ];
  try {
    const api = await getApi();
    const posts = await api.query(
      Prismic.Predicates.at('my.blog_template_groups.secondary_pinned', 'true'),
      {
        fetch,
        pageSize: 1,
        page: 1,
        orderings: '[my.blog_template_groups.publication_datetime desc]',
      },
    );
    post = formatPinnedPost(_get(posts, 'results[0]', {}));
  } catch (ex) {
    errorHandler(ex);
  }
  return post;
}

export function searchBlog(search) {
  if (!search) {
    return history.push('/blog');
  }

  return history.push(`/blog?search=${search}`);
}

export function createBlogQueryParams(search, tag, page) {
  const queryObject = {};
  if (tag) queryObject.tag = tag;
  if (search) queryObject.search = search;
  if (page > 1) queryObject.page = page;

  return queryString.stringify(queryObject, { sort: false });
}

export async function getProductPosts(slug = '', count = 2) {
  const fetch = [
    'blog_template_groups.title',
    'blog_template_groups.publication_datetime',
    'blog_template_groups.cover',
    'blog_template_groups.intro_text',
  ];
  let posts = [];
  try {
    const api = await getApi();
    const data = await api.query(
      [Prismic.Predicates.at('my.blog_template_groups.fanatical_slugs.slug', slug), Prismic.Predicates.not('my.blog_template_groups.category', 'News')],
      {
        fetch,
        pageSize: count,
        page: 0,
        orderings: '[my.blog_template_groups.publication_datetime desc]',
      },
    );
    posts = _get(data, 'results', []);
  } catch (ex) {
    errorHandler(ex);
  }
  return posts;
}

export async function getMultipleProductPosts(slugs, count = 3) {
  const fetch = [
    'blog_template_groups.title',
    'blog_template_groups.publication_datetime',
    'blog_template_groups.cover',
    'blog_template_groups.intro_text',
  ];
  let posts = [];
  try {
    const api = await getApi();
    const data = await api.query(
      [Prismic.Predicates.any('my.blog_template_groups.fanatical_slugs.slug', slugs), Prismic.Predicates.not('my.blog_template_groups.category', 'News')],
      {
        fetch,
        pageSize: count,
        page: 0,
        orderings: '[my.blog_template_groups.publication_datetime desc]',
      },
    );
    posts = _get(data, 'results', []);
  } catch (ex) {
    errorHandler(ex);
  }
  return posts;
}

export async function getFacetPosts(facet, slug, count = 3) {
  const fetch = [
    'blog_template_groups.title',
    'blog_template_groups.publication_datetime',
    'blog_template_groups.cover',
    'blog_template_groups.intro_text',
  ];
  let posts = [];
  try {
    const api = await getApi();
    const data = await api.query(
      [Prismic.Predicates.at(`my.blog_template_groups.${facet === 'combo' ? 'categories' : facet}_slugs.slug`, slug),
        Prismic.Predicates.not('my.blog_template_groups.category', 'News')],
      {
        fetch,
        pageSize: count,
        page: 0,
        orderings: '[my.blog_template_groups.publication_datetime desc]',
      },
    );
    posts = _get(data, 'results', []);
  } catch (ex) {
    errorHandler(ex);
  }
  return posts;
}
